/* Showing numbers, money and dates the way the people using this read them.
 *
 * The tenants are Indian SMEs, so money is grouped in lakhs and crores
 * (₹1,12,400, not ₹112,400) and dates are day-first. `Intl` with `en-IN` does
 * both, so this is a thin, cached wrapper rather than hand-rolled grouping.
 *
 * Money is never formatted from the browser's locale: an invoice in INR reads
 * the same whether it is opened in Karur or in Frankfurt, because it is the
 * document's currency that decides, not the reader's laptop.
 */

const LOCALE = 'en-IN'

/* Building an `Intl` formatter is expensive -- far more expensive than using
 * one -- and a list builds one per cell. A 50-row list with six columns was
 * spending most of its render inside these constructors on a mid-range phone.
 * They are immutable and there are only ever a handful of distinct shapes, so
 * they are made once and kept.
 */
const kept = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>()

function cached<T>(key: string, make: () => T): T {
  let found = kept.get(key)
  if (!found) { found = make() as never; kept.set(key, found) }
  return found as T
}

const money = new Map<string, Intl.NumberFormat>()

export function formatCurrency(value: unknown, currency = 'INR', precision = 2): string {
  const n = Number(value)
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) return ''
  const key = `${currency}:${precision}`
  let fmt = money.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(LOCALE, {
      style: 'currency', currency, minimumFractionDigits: precision, maximumFractionDigits: precision,
    })
    money.set(key, fmt)
  }
  return fmt.format(n)
}

export function formatNumber(value: unknown, precision?: number): string {
  const n = Number(value)
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) return ''
  return cached(`n:${precision}`, () => new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: precision, maximumFractionDigits: precision ?? 6,
  })).format(n)
}

export const formatPercent = (value: unknown) =>
  value === null || value === undefined || value === '' ? '' : `${formatNumber(value, 2)}%`

/** Frappe stores dates as `YYYY-MM-DD` and datetimes as `YYYY-MM-DD HH:mm:ss`,
 *  both in the site's timezone and without an offset. Handing those straight
 *  to `new Date()` makes the browser guess, and the guess differs between
 *  Safari and Chrome, so they are read apart instead. */
export function parseFrappeDate(value: unknown): Date | null {
  if (!value || typeof value !== 'string') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value)
  if (!m) return null
  const [, y, mo, d, h, mi, s] = m
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h ?? 0), Number(mi ?? 0), Number(s ?? 0))
}

export function formatDate(value: unknown): string {
  const d = parseFrappeDate(value)
  return d ? cached('d', () => new Intl.DateTimeFormat(LOCALE,
    { day: '2-digit', month: 'short', year: 'numeric' })).format(d) : ''
}

export function formatDatetime(value: unknown): string {
  const d = parseFrappeDate(value)
  return d ? cached('dt', () => new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })).format(d) : ''
}

/** "3 minutes ago". Used where the exact instant matters less than freshness. */
export function formatSince(value: unknown): string {
  const d = parseFrappeDate(value)
  if (!d) return ''
  const seconds = Math.round((Date.now() - d.getTime()) / 1000)
  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'], [3600, 'minute'], [86400, 'hour'], [2592000, 'day'], [31536000, 'month'],
  ]
  const rtf = cached('rtf', () => new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' }))
  let previous = 1
  for (const [limit, unit] of steps) {
    if (Math.abs(seconds) < limit) return rtf.format(-Math.round(seconds / previous), unit)
    previous = limit
  }
  return rtf.format(-Math.round(seconds / 31536000), 'year')
}

/** Duration is stored as seconds. `90061` is not a number anybody reads. */
export function formatDuration(value: unknown): string {
  const total = Math.max(0, Math.trunc(Number(value) || 0))
  const d = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return [d && `${d}d`, h && `${h}h`, m && `${m}m`, (!d && !h && s) ? `${s}s` : ''].filter(Boolean).join(' ') || '0s'
}

/** A Select's options arrive newline-separated; the leading blank means "not
 *  set", which is a different thing from an empty choice. */
export function selectOptions(options?: string): { value: string; label: string }[] {
  if (!options) return []
  return options.split('\n').map((raw) => {
    const value = raw.trim()
    return { value, label: value || '—' }
  })
}

export const DOCSTATUS_LABEL = ['Draft', 'Submitted', 'Cancelled'] as const
export const docstatusChip = (docstatus: unknown) =>
  docstatus === 1 ? 'chip-ok' : docstatus === 2 ? 'chip-danger' : 'chip-draft'
